import { Link } from "react-router-dom";

import { toGame } from "../../../common/navigation";
import { SEASON_TYPE_LABELS } from "../../../data/mockGames/mockGames";
import { sourceForGame } from "../../../data/dataSources";
import { readGameStatus } from "../../../utils/gameStatus";
import { gameStatusLabel } from "../../../utils/situation";
import GameMeta from "../GameMeta/GameMeta";
import Matchup from "../Matchup/Matchup";
import SourceBadge from "../SourceBadge/SourceBadge";
import StatusBadge from "../StatusBadge/StatusBadge";
import { gameWashStyle } from "../../../utils/teamWash";
import styles from "./GameCard.module.css";

/**
 * One game in the schedule (PRD §11, §12).
 *
 * Laid out like a broadcast matchup rather than a list row: the two clubs
 * balanced either side of a centre column, crest above abbreviation above
 * nickname. A viewer should be able to tell who is playing from across the
 * room without reading a sentence.
 *
 * The centre column is the only part that changes with state: kickoff
 * time before the game, the live score during and after it. Keeping the
 * clubs pinned either side means a card going live doesn't jump about.
 */
const GameCard = ({ game, status, actionLabel }) => {
  if (!game) return null;

  /* Real games carry their own status; the practice game stays "scheduled"
     until the viewer starts it. */
  const {
    status: effectiveStatus,
    live,
    final,
    offSchedule,
  } = readGameStatus(game, status);

  const action =
    actionLabel ??
    (game.isSimulated
      ? "Start the practice game"
      : live
        ? "Follow it live"
        : final
          ? "See the recap"
          : offSchedule
            ? "See what we know"
            : "Get ready");

  return (
    <article
      className={`${styles.GameCard} app-team-wash ${
        live ? styles.isLive : ""
      }`.trim()}
      style={gameWashStyle(game)}
    >
      <div className={styles.badges}>
        <span className={styles.badgesLeft}>
          <StatusBadge tone="accent">
            {SEASON_TYPE_LABELS[game.seasonType] ?? "Game"}
          </StatusBadge>
          {live && (
            <StatusBadge tone="live" pulse>
              Live
            </StatusBadge>
          )}
          {final && <StatusBadge tone="final">Final</StatusBadge>}
          {offSchedule && (
            <StatusBadge tone="warning">
              {gameStatusLabel(effectiveStatus)}
            </StatusBadge>
          )}
        </span>

        {/* Real NFL game, or our scripted practice one? The source mark
            answers it faster than any wording could. */}
        {game.isSimulated ? (
          <StatusBadge tone="warning" icon="fa-solid fa-flask">
            Practice
          </StatusBadge>
        ) : (
          <SourceBadge source={sourceForGame(game)} size="sm" showName={false} />
        )}
      </div>

      <Matchup
        as="h3"
        game={game}
        status={effectiveStatus}
        className={styles.matchup}
      />

      <GameMeta
        game={game}
        status={effectiveStatus}
        className={styles.meta}
      />

      <Link to={toGame(game.gameId)} className="btn btn-primary w-100">
        {action}
      </Link>
    </article>
  );
};

export default GameCard;
